import type { OrgPolicyStatus } from '@adminhub/graphql-types';

import type { FeatureEvents as BaseFeatureEvents, UIEvent as BaseUIEvent, ScreenEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

const screens = {
  encryptionLandingScreen: 'encryptionLandingScreen',
  encryptionPolicyProductsScreen: 'encryptionPolicyProductsScreen',
  encryptionPolicyCreationTaskSetUpEncryptionScreen: 'encryptionPolicyCreationTaskSetUpEncryptionScreen',
  encryptionPolicyCreationTaskEncryptionKeysScreen: 'encryptionPolicyCreationTaskEncryptionKeysScreen',
  encryptionPolicyCreationTaskContextTagScreen: 'encryptionPolicyCreationTaskContextTagScreen',
} as const;

type Screen = (typeof screens)[keyof typeof screens];

type EncryptionPageVersion = 'BYOK' | 'CMK';
type SectionExpanded = 'encryption-context-identifier' | 'vpc-endpoint';
type CmkPolicyCreatedSpotlight = 'tenantIdVpce' | 'apps';
type EncryptionStepId = 'intro' | 'encryption-keys' | 'context-identifier';

type EncryptionActionSubjectId = `encryption${string}`;

interface EncryptionUIEvent extends Omit<BaseUIEvent, 'data'> {
  data: Omit<BaseUIEvent['data'], 'actionSubjectId'> & {
    actionSubjectId: EncryptionActionSubjectId;
    source: Screen;
  };
}

type EncryptionFeatureEventsType = Omit<BaseFeatureEvents<Screen, string, never, never>, 'ui'> & {
  ui: Record<string, (...args: any[]) => EncryptionUIEvent>;
};

export const encryptionEvents = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/BYOK/pages/2806088458/BYOK+-+Front+End+Analytics+Spec',
  screen: {
    encryptionLandingScreen: (attributes: { canCreateCmk: boolean }) => ({
      data: {
        name: screens.encryptionLandingScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/56121',
    }),
    encryptionPolicyProductsScreen: (attributes: { policyStatus: OrgPolicyStatus; pageVersion: EncryptionPageVersion }): ScreenEvent => ({
      data: {
        name: screens.encryptionPolicyProductsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/56122',
    }),
    encryptionPolicyCreationTaskSetUpEncryptionScreen: () => ({
      data: {
        name: screens.encryptionPolicyCreationTaskSetUpEncryptionScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86232',
    }),
    encryptionPolicyCreationTaskEncryptionKeysScreen: () => ({
      data: {
        name: screens.encryptionPolicyCreationTaskEncryptionKeysScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86233',
    }),
    encryptionPolicyCreationTaskContextTagScreen: () => ({
      data: {
        name: screens.encryptionPolicyCreationTaskContextTagScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86234',
    }),
  },

  ui: {
    learnMore: (_: undefined, data: { source: Screen }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'encryptionLearnMoreLink',
        ...data,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82598',
    }),
    encryptionPolicyAppsScreenLearnMoreError: () => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'encryptionLearnMoreErrorLink',
        source: screens.encryptionPolicyProductsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82599',
    }),
    encryptionCmkTabClicked: (data: { tabName: string }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Tab,
        actionSubjectId: 'encryptionCmkTab',
        source: screens.encryptionPolicyProductsScreen,
        attributes: {
          tabName: data.tabName,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82201',
    }),
    encryptionUnderstandMoreAboutCmkLinkClicked: () => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'encryptionUnderstandMoreAboutCmkLink',
        source: screens.encryptionPolicyProductsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82351',
    }),
    encryptionUnderstandHowToKeepDataSecureLinkClicked: () => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'encryptionUnderstandHowToKeepDataSecureLinkClicked',
        source: screens.encryptionPolicyProductsScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82353',
    }),
    encryptionSeeSamplePolicyExpanded: (data: { sectionExpanded: SectionExpanded }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionSeeSamplePolicy',
        source: screens.encryptionPolicyProductsScreen,
        attributes: {
          sectionExpanded: data.sectionExpanded,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82364',
    }),
    funnelCmkCreationCreatePolicy: (_: undefined, data: { source: Screen }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionCreatePolicyButton',
        ...data,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85759',
    }),
    funnelCmkCreationBegin: (_: undefined, data: { source: Screen }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionBeginCmkConfigurationButton',
        ...data,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85853',
    }),
    funnelCmkCreationNext: (_: undefined, data: { source: Screen }, clientErrors: string[] | null, serverErrors: string[] | null) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionNextOnEncryptionKeysFormButton',
        source: data.source,
        attributes: {
          clientErrors,
          serverErrors,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85854',
    }),
    funnelCmkCreationEnroll: (_: undefined, data: { source: Screen }, clientErrors: string[] | null, serverErrors: string[] | null) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionEnrollInCmkEncryptionButton',
        source: data.source,
        attributes: {
          clientErrors,
          serverErrors,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85855',
    }),
    funnelCmkCreationSuccessFlagClosed: (_: undefined, data: { source: Screen }) => ({
      data: {
        action: Action.Closed,
        actionSubject: ActionSubject.Flag,
        actionSubjectId: 'encryptionSuccessFlagClose',
        ...data,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85856',
    }),
    funnelCmkCreationSpotlight: (_: undefined, activeSpotlight: CmkPolicyCreatedSpotlight) => ({
      data: {
        action: Action.Closed,
        actionSubject: ActionSubject.Spotlight,
        actionSubjectId: 'encryptionCmkPolicyCreatedSpotlight',
        source: 'encryptionPolicyProductsScreen',
        attributes: {
          activeSpotlight,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86329',
    }),
    encryptionCmkDocsLinkClicked: (_: undefined, data: { source: Screen; url: string }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Link,
        actionSubjectId: 'encryptionCmkDocsLink',
        source: data.source,
        attributes: {
          url: data.url,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86311',
    }),
    dwellOnSetUpEncryptionScreen: () => ({
      data: {
        action: Action.Dwelled,
        actionSubject: ActionSubject.Page,
        actionSubjectId: 'encryptionPolicyCreationTaskSetUpEncryptionScreenDwelled',
        source: screens.encryptionPolicyCreationTaskSetUpEncryptionScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86314',
    }),
    dwellOnEncryptionKeysScreen: () => ({
      data: {
        action: Action.Dwelled,
        actionSubject: ActionSubject.Page,
        actionSubjectId: 'encryptionPolicyCreationTaskEncryptionKeysScreenDwelled',
        source: screens.encryptionPolicyCreationTaskEncryptionKeysScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86315',
    }),
    dwellOnContextTagScreen: () => ({
      data: {
        action: Action.Dwelled,
        actionSubject: ActionSubject.Page,
        actionSubjectId: 'encryptionPolicyCreationTaskContextTagScreenDwelled',
        source: screens.encryptionPolicyCreationTaskContextTagScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86316',
    }),
    backButtonClicked: (_: undefined, data: { source: Screen }, stepId: EncryptionStepId) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionPolicyCreationTaskBackButton',
        source: data.source,
        attributes: {
          stepId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86325',
    }),
    exploreCmkSkuButtonClicked: (_: undefined, data: { source: Screen }) => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'encryptionExploreCmkSkuButton',
        source: data.source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/87611',
    }),
  },
  track: {
    encryptionCmkPolicyCreated: (policyId: string, regionId: string, numOfKeys: number) => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.CmkPolicy,
        source: screens.encryptionPolicyCreationTaskContextTagScreen,
        attributes: {
          policyId,
          regionId,
          numOfKeys,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/86333',
    }),
  },
  operational: {},
} satisfies EncryptionFeatureEventsType;
